import type {
	BodyCreateTicketApiV1TicketsPost,
	UserModel,
} from '@/shared/generated'
import type { z } from 'zod'

import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { showNewMessage } from '@intercom/messenger-js-sdk'
import { MessageCircleMoreIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useSession } from '@/entities/session'
import {
	ticketSchema,
	TicketTypeField,
	useCreateSupportTicketMutation,
} from '@/entities/ticket'
import { getUsername } from '@/entities/user'

import {
	FormProvider,
	RHFFileInput,
	RHFTextArea,
	RHFTextField,
} from '@/shared/components/HookForm'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button } from '@/shared/components/ui/button'
import { FieldGroup, FieldSet } from '@/shared/components/ui/field'
import {
	Item,
	ItemActions,
	ItemContent,
	ItemDescription,
	ItemTitle,
} from '@/shared/components/ui/item'
import { Loader } from '@/shared/components/ui/loader'
import {
	ModalBody,
	ModalClose,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Spinner } from '@/shared/components/ui/spinner'
import { ampli } from '@/shared/lib/ampli'
import Sentry from '@/shared/lib/sentry'
import { processingMessage } from '@/shared/tools/message'
import { getProject } from '@/shared/tools/utils'

export default function ReportProblem({
	close,
	requesttype = undefined,
}: {
	close(): void
	requesttype?: string
}) {
	const { t } = useTranslation()

	const { user } = useSession()
	const methods = useForm<FormData>({
		resolver: zodResolver(ticketSchema),
		defaultValues: {
			requesttype,
		},
	})
	useEffect(() => {
		ampli.jsdCreateTicketOpenModal()
	}, [])
	const createSupportTicket = useCreateSupportTicketMutation()
	const { isPending } = createSupportTicket
	const onSubmit = async (data: FormData) => {
		ampli.jsdCreateTicketSubmitForm({
			attachments: data.attachments?.length ?? 0,
		})
		try {
			const email = user?.email ?? ''
			const username = getUsername(user as UserModel) ?? ''
			const project = getProject()
			const requestBody: BodyCreateTicketApiV1TicketsPost = {
				summary: data.summary,
				description: `
				${data.message}

				|*Email*|${email}|
				|*Name*|${username}|
				|*Project*|${project}|
				|*Reported from*|${window.location.href}|
				`,
				request_type_id: data.requesttype,
				attachments: data.attachments || [],
			}

			toast.promise(createSupportTicket.mutateAsync(requestBody), {
				loading: processingMessage(t),
				success: t('support.ticket.created'),
				error: (error) => {
					Sentry.captureException(error)

					return t('support.ticket.error', {
						defaultValue: 'Error on creating ticket',
					})
				},
			})
		} catch (error) {
			Sentry.captureException(error)
		}
	}

	return (
		<>
			<ModalHeader>
				<ModalTitle>
					{t('support.window.title', { defaultValue: 'Operational support' })}
				</ModalTitle>
				<ModalDescription>
					{t('support.window.subtitle', {
						defaultValue:
							'We are here to help you with any questions you may have.',
					})}
				</ModalDescription>
			</ModalHeader>
			{createSupportTicket.data && (
				<Alert variant='success'>
					<AlertDescription>
						{t('support.ticket.created')}{' '}
						<a
							href={createSupportTicket.data._links?.web}
							target='_blank'
							rel='noreferrer'
						>
							{createSupportTicket.data.issueKey}
						</a>
					</AlertDescription>
				</Alert>
			)}
			<ModalBody className='flex flex-col gap-3'>
				<Blanket
					isTinted={isPending}
					className='absolute flex items-center justify-center bg-white/50 backdrop-blur-xs dark:bg-black/50'
				>
					<Loader />
				</Blanket>
				<Item variant='muted'>
					<ItemContent>
						<ItemTitle>
							{t('support.window.form.subtitle_1', {
								defaultValue: 'What can we help you with?',
							})}
						</ItemTitle>
						<ItemDescription>
							{t('support.window.form.subtitle_2', {
								defaultValue:
									'You will get the answer to your request by email shortly after one of us receives your message.',
							})}
						</ItemDescription>
					</ItemContent>
					<ItemActions>
						<Button
							onClick={() => {
								ampli.jsdCreateTicketOpenIntercom()
								showNewMessage(methods.watch('message'))
							}}
						>
							<MessageCircleMoreIcon />
							{t('app.live_chat', { defaultValue: 'Live chat' })}
						</Button>
					</ItemActions>
				</Item>
				<FormProvider
					methods={methods}
					onSubmit={methods.handleSubmit(onSubmit)}
				>
					<FieldGroup>
						<FieldSet disabled={Boolean(isPending || createSupportTicket.data)}>
							<TicketTypeField />
							<RHFTextField
								placeholder={t('support.window.form.summary.placeholder', {
									defaultValue: 'What does your issue mainly connect with?',
								})}
								required
								autoComplete='off'
								autoCorrect='off'
								className='w-full'
								label={t('support.window.form.summary.label', {
									defaultValue: 'Subject',
								})}
								name='summary'
							/>
							<RHFTextArea
								placeholder={t('support.window.form.message.placeholder', {
									defaultValue: 'Describe your issue in details',
								})}
								required
								rows={5}
								autoComplete='off'
								autoCorrect='off'
								className='w-full'
								label={t('support.window.form.message.label', {
									defaultValue: 'Message',
								})}
								name='message'
							/>
							<RHFFileInput
								label={
									t('support.window.form.attachment.label', {
										defaultValue: 'Attachment',
									}) + ' (optional)'
								}
								className='cursor-pointer'
								name='attachments'
							/>
						</FieldSet>
					</FieldGroup>
				</FormProvider>
			</ModalBody>
			<ModalFooter className='justify-between'>
				<ModalClose asChild>
					<Button>{t('general_cancel')}</Button>
				</ModalClose>
				<Button
					variant='primary'
					disabled={Boolean(isPending || createSupportTicket.data)}
					onClick={methods.handleSubmit(onSubmit)}
				>
					{isPending && <Spinner />}
					{t('general_send', { defaultValue: 'Send' })}
				</Button>
			</ModalFooter>
		</>
	)
}

type FormData = z.infer<typeof ticketSchema>
